import { Head, Link, router, useForm } from "@inertiajs/react"
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  Shield,
  MessageSquare,
  Users,
  Search,
  Pencil,
  Trash2,
  Plus,
  AlertCircle,
  EyeOff,
  Eye,
  Send,
  Globe,
  FileText,
} from "lucide-react"
import { useEffect, useState } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { CourseCoverImage } from "@/components/CourseCoverImage"
import { CourseFilesList } from "@/components/CourseFilesList"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// `answeredAt` is updated_at, not created_at: the member write path upserts in
// place, so created_at would date an answer to a draft that no longer exists.
function formatAnswerDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function ProgressBar({ value, max }) {
  const percent = max > 0 ? (value / max) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-kf-red rounded-full transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs text-zinc-500 shrink-0">
        {value}/{max}
      </span>
    </div>
  )
}

// "What did my people write here." Answers are looked up by question id from
// the top-level `lessonAnswers` prop rather than read off the lesson object:
// question_responses has no lesson_id, and a nested array could never be
// deferred or partially reloaded.
//
// A question with no answers renders muted rather than being omitted. A
// question omitted for being blank is indistinguishable from one that does not
// exist, which is the dead end this dialog exists to fix.
function LessonAnswersDialog({ open, onOpenChange, lesson, lessonAnswers }) {
  const [search, setSearch] = useState("")

  if (!lesson) return null

  const questions = lesson.questions || []
  const term = search.trim().toLowerCase()

  const rows = questions.map((q) => {
    const answers = lessonAnswers?.[String(q.id)] || []
    return {
      ...q,
      total: answers.length,
      answers: term
        ? answers.filter((a) => a.memberName.toLowerCase().includes(term))
        : answers,
    }
  })

  const anyAnswers = rows.some((row) => row.total > 0)

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Answers: {lesson.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {anyAnswers
              ? "What the members you coach wrote inside this lesson."
              : "Nobody has answered yet. These are the questions waiting for them."}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {questions.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4">This lesson has no questions.</p>
        ) : (
          <div className="space-y-6 px-4 pb-4 sm:px-0 sm:pb-0">
            {anyAnswers && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  type="search"
                  placeholder="Search by member name..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}

            {rows.map((question) => (
              <div key={question.id} className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-900">{question.text}</h4>
                {question.answers.length > 0 ? (
                  <div className="space-y-2">
                    {question.answers.map((answer) => (
                      <div key={answer.id} className="bg-zinc-50 rounded-lg p-3 space-y-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-xs font-medium text-zinc-700">
                            {answer.memberName}
                          </p>
                          {answer.answeredAt && (
                            <p className="text-xs text-zinc-400 shrink-0">
                              Answered {formatAnswerDate(answer.answeredAt)}
                            </p>
                          )}
                        </div>
                        <p className="text-sm text-zinc-600 whitespace-pre-line">
                          {answer.responseText}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400 italic">
                    {term && question.total > 0 ? "No matching members." : "No answers yet."}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Lesson form dialog (used for both create and edit). Mirrors the admin
// Show page's LessonFormDialog so the field layout is consistent.
// ---------------------------------------------------------------------------

function LessonFormDialog({ open, onOpenChange, courseId, lesson = null, nextPosition = 1 }) {
  const isEditing = !!lesson
  const form = useForm({
    title: lesson?.title || "",
    position: lesson?.position ?? (isEditing ? "" : nextPosition),
    status: lesson?.status || "draft",
    coach_only: lesson?.coachOnly || false,
  })

  useEffect(() => {
    if (open && !isEditing) {
      form.setData("position", String(nextPosition))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, nextPosition])

  const handleSubmit = (e) => {
    e.preventDefault()

    const options = {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
        if (!isEditing) form.reset()
      },
    }

    if (isEditing) {
      form.patch(`/app/coach/courses/${courseId}/lessons/${lesson.id}`, options)
    } else {
      form.post(`/app/coach/courses/${courseId}/lessons`, options)
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Lesson" : "Add Lesson"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update lesson details."
                : "Add a new lesson to this course."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="lesson-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lesson-title"
                placeholder="Lesson title"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
                required
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lesson-position">Position</Label>
              <Input
                id="lesson-position"
                type="number"
                min="0"
                placeholder="e.g. 1"
                value={form.data.position}
                onChange={(e) => form.setData("position", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.data.status}
                onValueChange={(v) => form.setData("status", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Coach Only</Label>
                <p className="text-xs text-zinc-400">
                  Only visible to coaches when enabled.
                </p>
              </div>
              <Switch
                checked={form.data.coach_only}
                onCheckedChange={(checked) => form.setData("coach_only", checked)}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                ? "Save Changes"
                : "Add Lesson"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CourseShow({
  currentUser,
  course,
  lessons,
  lessonAnswers = {},
  answerCounts = {},
  memberProgress,
  coachGroup,
  coachGroups = [],
  assignedGroupIds = [],
}) {
  const [answersLesson, setAnswersLesson] = useState(null)
  const [addLessonOpen, setAddLessonOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteLesson, setDeleteLesson] = useState(null)
  const [submittingForReview, setSubmittingForReview] = useState(false)
  const [assignBusyGroupId, setAssignBusyGroupId] = useState(null)

  const assignedGroupIdSet = new Set(assignedGroupIds || [])
  const isPublished = course.status === "published"

  const handleAssignGroup = (groupId) => {
    if (assignBusyGroupId) return
    setAssignBusyGroupId(groupId)
    router.post(
      `/app/coach/courses/${course.id}/assign`,
      { group_id: groupId, return_to: "course" },
      { preserveScroll: true, onFinish: () => setAssignBusyGroupId(null) }
    )
  }

  const handleUnassignGroup = (groupId) => {
    if (assignBusyGroupId) return
    setAssignBusyGroupId(groupId)
    router.delete(`/app/coach/courses/${course.id}/unassign`, {
      data: { group_id: groupId, return_to: "course" },
      preserveScroll: true,
      onFinish: () => setAssignBusyGroupId(null),
    })
  }

  const totalLessons = lessons?.length || 0
  const canEdit = !!course.canEdit

  const formatDate = (iso) => {
    if (!iso) return null
    try {
      return new Date(iso).toLocaleDateString("en-CA")
    } catch {
      return null
    }
  }

  const handleSubmitForPublicReview = () => {
    if (submittingForReview) return
    setSubmittingForReview(true)
    router.post(
      `/app/coach/courses/${course.id}/submit_for_public_review`,
      {},
      {
        preserveScroll: true,
        onFinish: () => setSubmittingForReview(false),
      }
    )
  }

  const handleDeleteCourse = () => {
    router.delete(`/app/coach/courses/${course.id}`, {
      onSuccess: () => setDeleteOpen(false),
    })
  }

  const handleDeleteLesson = () => {
    if (!deleteLesson) return
    router.delete(
      `/app/coach/courses/${course.id}/lessons/${deleteLesson.id}`,
      {
        preserveScroll: true,
        onSuccess: () => setDeleteLesson(null),
      }
    )
  }

  const handleReorder = (lesson, newPosition) => {
    router.patch(
      `/app/coach/courses/${course.id}/lessons/${lesson.id}/reorder`,
      { position: newPosition },
      { preserveScroll: true }
    )
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${course.title} - Courses - Kingdom Factor`} />

      <div className="space-y-6">
        {/* Back link */}
        <Link
          href="/app/coach/courses"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Courses
        </Link>

        {/* Rejection banner — visible only to the author when the course was
            rejected during admin review. Shows the admin-supplied reason and
            offers a direct CTA to fix and resubmit (resubmit comes via the
            edit flow + U5's submit button). */}
        {canEdit && course.publicStatus === "rejected" && course.rejectionReason && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div className="space-y-2 flex-1">
                  <p className="font-medium text-red-900">
                    This course was not approved for public review.
                  </p>
                  <p className="text-sm text-red-800">
                    <span className="font-medium">Reason:</span> {course.rejectionReason}
                  </p>
                  <Button asChild variant="outline" size="sm">
                    <Link href={`/app/coach/courses/${course.id}/edit`}>
                      Edit course
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Course header */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Cover image */}
          <div className="w-full md:w-64 shrink-0">
            <div className="aspect-[3/2] bg-zinc-100 rounded-lg overflow-hidden">
              <CourseCoverImage
                url={course.coverImageUrl}
                alt={course.title}
                wrapperClassName="h-full w-full"
                imgClassName="w-full h-full object-cover"
                fallbackIconClassName="h-10 w-10 text-zinc-300"
              />
            </div>
          </div>

          <div className="flex-1 space-y-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-2 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-zinc-900">{course.title}</h1>
                  {course.category && (
                    <Badge variant="secondary">{course.category.name}</Badge>
                  )}
                  {course.publicStatus === "pending_review" && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                      Pending public review
                      {course.submittedAt && (
                        <> &middot; submitted {formatDate(course.submittedAt)}</>
                      )}
                    </Badge>
                  )}
                  {course.publicStatus === "public" && (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      Public
                    </Badge>
                  )}
                </div>
                {course.description && (
                  <p className="text-zinc-600">{course.description}</p>
                )}
                <p className="text-sm text-zinc-500">
                  {totalLessons} {totalLessons === 1 ? "lesson" : "lessons"}
                  {coachGroup?.name && (
                    <>
                      {" "}&middot; Assigned to {coachGroup.name}
                    </>
                  )}
                </p>
              </div>

              {/* Owner-only edit/delete actions */}
              {canEdit && (
                <div className="flex items-center gap-2 shrink-0">
                  <Button asChild variant="outline" size="sm">
                    <Link href={`/app/coach/courses/${course.id}/edit`}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit course
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Assign to your groups (owner-only). Creating the GroupCourse link
            here is what makes the course visible to a group's members — before
            this, own courses had no reachable assign affordance (tasks #337). */}
        {canEdit && (
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Assign to your groups</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {!isPublished ? (
                <p className="text-sm text-zinc-500">
                  Publish this course to assign it to your groups. Members only
                  see published courses that are assigned to their group.
                </p>
              ) : coachGroups.length === 0 ? (
                <p className="text-sm text-zinc-500">
                  You don&rsquo;t coach any groups yet.
                </p>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {coachGroups.map((g) => {
                    const assigned = assignedGroupIdSet.has(g.id)
                    const busy = assignBusyGroupId === g.id
                    return (
                      <div
                        key={g.id}
                        className="flex items-center justify-between gap-3 py-2.5"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-zinc-800 truncate">
                            {g.name}
                          </span>
                          {assigned && (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              Assigned
                            </Badge>
                          )}
                        </div>
                        {assigned ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => handleUnassignGroup(g.id)}
                          >
                            {busy ? "Removing…" : "Remove"}
                          </Button>
                        ) : (
                          <Button
                            variant="gradient"
                            size="sm"
                            disabled={busy}
                            onClick={() => handleAssignGroup(g.id)}
                          >
                            {busy ? "Assigning…" : "Assign"}
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Submit for Public Review */}
        {course.canSubmitForPublicReview && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-zinc-900">
                  Make this course available to all coaches
                </h3>
                <p className="text-sm text-zinc-600">
                  Submit for public review. An admin will review and either
                  approve it for the public catalog or reject with feedback you
                  can address.
                </p>
              </div>
              <Button
                variant="gradient"
                onClick={handleSubmitForPublicReview}
                disabled={submittingForReview}
              >
                <Send className="h-4 w-4" />
                {submittingForReview ? "Submitting..." : "Submit for Public Review"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Public — approved indicator */}
        {course.publicStatus === "public" && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 flex items-start gap-3">
              <Globe className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-emerald-900">
                  Public &mdash; available to all coaches
                </p>
                {(course.reviewedBy?.name || course.reviewedAt) && (
                  <p className="text-xs text-emerald-800">
                    Approved
                    {course.reviewedBy?.name && (
                      <> by {course.reviewedBy.name}</>
                    )}
                    {course.reviewedAt && (
                      <> on {formatDate(course.reviewedAt)}</>
                    )}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lessons */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Lessons</CardTitle>
              {canEdit && (
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={() => setAddLessonOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Lesson
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {lessons?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">#</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                    {canEdit && (
                      <TableHead className="hidden sm:table-cell w-28">
                        Coach Only
                      </TableHead>
                    )}
                    <TableHead className="w-44">Member answers</TableHead>
                    {canEdit && (
                      <TableHead className="text-right w-20">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lessons.map((lesson) => {
                    const questionCount = lesson.questions?.length || 0
                    // From `answerCounts`, which is always eager. Summing the
                    // answer text instead would make every row read "No answers
                    // yet" until a dialog had been opened.
                    const answerCount =
                      lesson.questions?.reduce(
                        (sum, q) => sum + (answerCounts[String(q.id)] || 0),
                        0
                      ) || 0

                    return (
                      <TableRow
                        key={lesson.id}
                        className={canEdit ? "cursor-pointer hover:bg-zinc-50" : ""}
                        onClick={
                          canEdit
                            ? () =>
                                router.visit(
                                  `/app/coach/courses/${course.id}/lessons/${lesson.id}`
                                )
                            : undefined
                        }
                      >
                        <TableCell className="text-zinc-500">
                          {canEdit ? (
                            <Input
                              type="number"
                              min="0"
                              defaultValue={lesson.position ?? ""}
                              className="w-16 h-8 text-center"
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => {
                                const val = parseInt(e.target.value, 10)
                                if (!isNaN(val) && val !== lesson.position) {
                                  handleReorder(lesson, val)
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") e.target.blur()
                              }}
                            />
                          ) : (
                            <span className="font-mono text-xs">
                              {lesson.position}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{lesson.title}</span>
                            {!canEdit && lesson.coachOnly && (
                              <Badge
                                variant="outline"
                                className="text-xs border-amber-300 text-amber-700 bg-amber-50"
                              >
                                <Shield className="h-3 w-3 mr-1" />
                                Coach Only
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              lesson.status === "published" ? "default" : "secondary"
                            }
                            className={
                              lesson.status === "published"
                                ? "bg-green-100 text-green-800 hover:bg-green-100"
                                : ""
                            }
                          >
                            {lesson.status === "published" ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : null}
                            {lesson.status}
                          </Badge>
                        </TableCell>
                        {canEdit && (
                          <TableCell className="hidden sm:table-cell">
                            {lesson.coachOnly ? (
                              <Badge
                                variant="outline"
                                className="border-amber-500 text-amber-600"
                              >
                                <EyeOff className="h-3 w-3 mr-1" />
                                Coach
                              </Badge>
                            ) : (
                              <span className="text-xs text-zinc-400">
                                <Eye className="h-3 w-3 inline mr-1" />
                                All
                              </span>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {questionCount > 0 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={(e) => {
                                e.stopPropagation()
                                setAnswersLesson(lesson)
                              }}
                            >
                              <MessageSquare className="h-3.5 w-3.5 mr-1" />
                              {/* Zero stays CLICKABLE. "0 responses" read as a
                                  stat and dead-ended the coach who asked for
                                  this; the dialog behind it shows the questions
                                  still waiting, which is useful before a
                                  session. */}
                              {answerCount === 0
                                ? "No answers yet"
                                : `${answerCount} ${answerCount === 1 ? "answer" : "answers"}`}
                            </Button>
                          ) : (
                            <span className="text-xs text-zinc-400">
                              No questions
                            </span>
                          )}
                        </TableCell>
                        {canEdit && (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteLesson(lesson)
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <BookOpen className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">
                  {canEdit
                    ? "No lessons yet. Add your first lesson to start building this course."
                    : "No published lessons yet."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Course Files (legacy imports; card hidden when the course has none) */}
        {course.files?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Course Files
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CourseFilesList files={course.files} />
            </CardContent>
          </Card>
        )}

        {/* Member Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Member Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {memberProgress?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead className="w-64">Progress</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberProgress.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        {/* The roster was a dead end: the member page is where
                            one person's answers live. Same entry point as
                            Users/Index and Group/Show. */}
                        <Link
                          href={`/app/coach/members/${member.id}`}
                          className="block hover:underline"
                        >
                          <p className="font-medium text-zinc-900">{member.name}</p>
                          <p className="text-xs text-zinc-500">{member.email}</p>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <ProgressBar
                          value={member.lessonsCompleted}
                          max={member.totalLessons}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <Users className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No members in this group yet.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Member answers */}
      <LessonAnswersDialog
        open={!!answersLesson}
        onOpenChange={(open) => {
          if (!open) setAnswersLesson(null)
        }}
        lesson={answersLesson}
        lessonAnswers={lessonAnswers}
      />

      {/* Add lesson */}
      <LessonFormDialog
        open={addLessonOpen}
        onOpenChange={setAddLessonOpen}
        courseId={course.id}
        nextPosition={
          (lessons?.reduce((max, l) => Math.max(max, l.position ?? 0), 0) ?? 0) + 1
        }
      />

      {/* Delete course */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Course</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{course.title}&rdquo;? This
              will also remove all lessons, questions, and resources. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCourse}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete lesson */}
      <AlertDialog
        open={!!deleteLesson}
        onOpenChange={(open) => !open && setDeleteLesson(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lesson</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteLesson?.title}&rdquo;?
              This will also remove all questions and resources for this lesson.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLesson}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Lesson
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
